
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Progress } from "@/components/ui/progress"
import { 
  ChevronDown, 
  ChevronRight, 
  Play, 
  Pause,
  Users,
  Clock,
  BarChart3,
  Kanban,
  Calendar,
  Search,
  Filter,
  Plus,
  Eye,
  ArrowRight,
  Settings,
  ArrowLeft
} from "lucide-react"
import { cn } from "@/lib/utils"
import { SingleLaneGantt } from "@/components/workflow/gantt"
import { getUnifiedOrders, getOrderDataList, stages as masterStages, departments as masterDepartments } from "@/data/masterData"
import { toast } from "sonner"

type ViewMode = "kanban" | "gantt"


const Workflow = () => {
  const navigate = useNavigate()
  const [currentWorkflow, setCurrentWorkflow] = useState("standard")
  const [viewMode, setViewMode] = useState<ViewMode>("kanban")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedPriority, setSelectedPriority] = useState("all")
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null)
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set(['ORD-001', 'ORD-003']))
  const [expandedOrderStages, setExpandedOrderStages] = useState<Set<string>>(new Set(['ORD-001', 'ORD-003']))
  const [expandedStageDepartments, setExpandedStageDepartments] = useState<Set<string>>(new Set(['ORD-001-new-order', 'ORD-003-production']))
  const [timelineOffset, setTimelineOffset] = useState(0)

  // Production workflow data source
  const DAYS = 14
  const allOrders = getUnifiedOrders()
  const stages = masterStages
  const departments = masterDepartments

  // Enhanced timeline calculation
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - 2 + timelineOffset)

  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders)
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId)
    } else {
      newExpanded.add(orderId)
    }
    setExpandedOrders(newExpanded)
  }

  const toggleOrderStageExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrderStages)
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId)
    } else {
      newExpanded.add(orderId)
    }
    setExpandedOrderStages(newExpanded)
  }

  const toggleStageDepartmentExpansion = (stageKey: string) => {
    const newExpanded = new Set(expandedStageDepartments)
    if (newExpanded.has(stageKey)) {
      newExpanded.delete(stageKey)
    } else {
      newExpanded.add(stageKey)
    }
    setExpandedStageDepartments(newExpanded)
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">工作流程監控</h1>
          <p className="text-muted-foreground">
            監控和管理所有工作流程的執行狀態
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-4 w-4 mr-2" />
            流程設定
          </Button>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            新增訂單
          </Button>
        </div>
      </div>

      {/* Workflow Tabs */}
      <Tabs value="standard" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="standard">標準訂單流程</TabsTrigger>
          <TabsTrigger value="rework">重工流程</TabsTrigger>
          <TabsTrigger value="routine">例行工作</TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="mt-6 space-y-6">
          {/* Workflow Controls */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">標準訂單流程</h2>
              <p className="text-muted-foreground">一般訂單的標準處理流程</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "kanban" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("kanban")}
              >
                <Kanban className="h-4 w-4 mr-1" />
                看板視圖
              </Button>
              <Button
                variant={viewMode === "gantt" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("gantt")}
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                甘特圖
              </Button>
            </div>
          </div>

          {/* View mode content */}
          {viewMode === "kanban" ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {stages.map((stage) => {
                const stageOrders = allOrders.filter(order => order.currentStageId === stage.id)
                const stageDepartments = stage.responsibleDepartments || []

                return (
                  <div 
                    key={stage.id} 
                    className="min-w-[280px] w-[280px] sm:min-w-[300px] sm:w-[300px] bg-gray-50 dark:bg-neutral-800/50 rounded-lg border border-gray-200 dark:border-neutral-700 flex flex-col h-[calc(100vh-280px)]"
                  >
                    {/* Column Header */}
                    <div className="p-4 bg-white dark:bg-neutral-900 rounded-t-lg border-b border-gray-200 dark:border-neutral-700 flex-shrink-0">
                      <div className="flex justify-between items-center">
                        <h2 className="text-sm font-medium flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                          {stage.name}
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({stageOrders.length})
                          </span>
                        </h2>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Users className="h-3 w-3" />
                        <span>{stageDepartments.length} 個部門</span>
                      </div>
                    </div>

                    {/* Scrollable Content Area */}
                    <div className="p-2 flex-grow overflow-y-auto">
                      {stageOrders.map((order) => (
                        <Card 
                          key={order.id} 
                          className="mb-2 cursor-pointer hover:shadow-md transition-all hover:scale-[1.02]"
                          onClick={() => setSelectedOrder(order.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium text-sm">{order.orderNumber || order.id}</h4>
                                  <Badge variant="outline" className="text-xs">
                                    {order.productCategory}類
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{order.customer}</p>
                                <p className="text-xs font-mono text-muted-foreground">{order.drawingNumber}</p>
                              </div>
                              <Badge 
                                variant={
                                  order.priority === "urgent" ? "destructive" : 
                                  order.priority === "high" ? "destructive" : 
                                  order.priority === "medium" ? "default" : "secondary"
                                } 
                                className="text-xs ml-2"
                              >
                                {order.priority === "urgent" ? "緊急" : 
                                 order.priority === "high" ? "高" : 
                                 order.priority === "medium" ? "中" : "低"}
                              </Badge>
                            </div>
                            
                            <div className="space-y-1.5 text-xs mb-3">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <span className="font-medium">產品:</span> 
                                <span className="truncate">{order.product}</span>
                              </div>
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <span className="font-medium">規格:</span> 
                                <span className="font-mono text-xs">{order.dimensions}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <span className="font-medium">訂購:</span> {order.quantity}
                                </div>
                                <div className="flex items-center gap-1 text-green-600">
                                  <span className="font-medium">完成:</span> {order.completedQuantity}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span className="font-medium">交期:</span> {new Date(order.dueDate).toLocaleDateString('zh-TW')}
                              </div>
                              {order.specialRequirements && (
                                <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                  <span className="font-medium">特殊要求:</span> 
                                  <span className="truncate">{order.specialRequirements}</span>
                                </div>
                              )}
                            </div>

                            {/* Department Progress */}
                            <div className="space-y-1.5 pt-2 border-t border-gray-100 dark:border-neutral-800">
                              {/* 顯示當前階段的部門資訊 */}
                              {order.stages
                                .find(s => s.stageId === stage.id)
                                ?.departments.map((dept) => (
                                  <div 
                                    key={dept.departmentId} 
                                    className="flex items-center gap-2 p-1.5 bg-muted/40 rounded text-xs hover:bg-muted/60 transition-colors"
                                  >
                                    <div className={`w-2 h-2 rounded-full ${departments.find(d => d.id === dept.departmentId)?.color} flex-shrink-0`}></div>
                                    <span className="flex-1 truncate">{dept.departmentName}</span>
                                    <div className="flex items-center gap-1">
                                      <Progress value={dept.progress * 100} className="w-8 h-1" />
                                      <Badge variant="outline" className="text-xs h-5 px-1">
                                        {Math.round(dept.progress * 100)}%
                                      </Badge>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {/* Empty State */}
                      {stageOrders.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                          <Kanban className="h-8 w-8 mb-2 opacity-50" />
                          <p className="text-xs">無訂單</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="overflow-x-auto">
                {/* Timeline Controls */}
                <div className="flex items-center justify-between mb-6 p-4 bg-muted/20 rounded-lg">
                  <div>
                    <h3 className="text-lg font-semibold">單一車道甘特圖</h3>
                    <p className="text-sm text-muted-foreground">點擊訂單可展開階段，點擊階段可展開部門，點擊部門可展開工序</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTimelineOffset(timelineOffset - 7)}
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      上週
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTimelineOffset(0)}
                    >
                      今天
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTimelineOffset(timelineOffset + 7)}
                    >
                      下週
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>

                {/* Single Lane Gantt Chart */}
                <SingleLaneGantt 
                  orders={getOrderDataList()}
                  startDate={startDate}
                  days={DAYS}
                  onTimeUpdate={(level, id, newStart, newEnd) => {
                    console.log('Time update:', { level, id, newStart, newEnd });
                    // 這裡可以實現時間更新邏輯
                  }}
                  onDueMarkerClick={(orderId) => {
                    toast.info(`點擊交期標記: ${orderId}`, {
                      description: "可以在此打開訂單詳情或編輯交期"
                    });
                  }}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default Workflow
